// Botão (DESIGN §5.1). Um `primario` por tela. Carregando: aria-busy, largura preservada, spinner + gerúndio.
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link, type LinkProps } from "react-router";
import { cn } from "../lib/cn";
import type { TipoIcone } from "./icones";

export type VarianteBotao =
  "primario" | "tinta" | "secundario" | "quieto" | "texto" | "perigo" | "sobre-tinta" | "discreto";
export type TamanhoBotao = "padrao" | "compacto";

const BASE =
  "relative inline-flex items-center justify-center gap-2 rounded-controle select-none " +
  "transition-colors duration-(--gs-dur-toque) ease-gs disabled:opacity-40 disabled:pointer-events-none " +
  "aria-disabled:opacity-40 aria-disabled:pointer-events-none";

const VARIANTES: Record<VarianteBotao, string> = {
  primario: "bg-acao text-sobre-acao hover:bg-acao-hover active:bg-acao-press",
  tinta: "bg-tinta text-sobre-tinta hover:bg-tinta-2 active:bg-tinta-2",
  secundario: "bg-painel text-tinta border border-fio-controle hover:bg-recuo active:bg-recuo",
  quieto: "bg-recuo text-tinta hover:bg-fio active:bg-fio",
  texto:
    "h-toque px-2 rounded-celula text-acao-texto text-apoio font-semibold underline-offset-4 hover:underline",
  perigo: "bg-painel text-erro border border-erro hover:bg-erro-fundo active:bg-erro-fundo",
  "sobre-tinta": "bg-tinta-2 text-sobre-tinta foco-sobre-tinta active:opacity-80",
  // Última opção de uma pilha, quase sempre destrutiva e com confirmação (DESIGN §5.1; treino-nao-enviado §9).
  // Na pilha da folha: centralizado e com largura pelo conteúdo (`self-center`), não ocupa a largura toda.
  discreto: "h-toque self-center px-3 rounded-celula text-texto-2 text-apoio font-medium hover:text-tinta hover:bg-recuo",
};

/** Variantes com altura e texto próprios (não recebem o tamanho padrão/compacto nem a largura total). */
const SEM_TAMANHO: ReadonlySet<VarianteBotao> = new Set(["texto", "discreto"]);

const TAMANHOS: Record<TamanhoBotao, string> = {
  padrao: "h-controle px-5 text-botao",
  // 40 de altura visual com área de toque 48 (só no painel). Texto 14/600, ícone 16 e sem quebra de linha,
  // como em todas as pranchas do painel (QA fidelidade A-1).
  compacto:
    "h-10 px-4 text-apoio font-semibold whitespace-nowrap after:absolute after:-inset-1 after:content-['']",
};

/** Ícone ao lado do texto: 20 no botão padrão (DESIGN §5.1), 16 no compacto (pranchas do painel). */
const iconeDoTamanho = (tamanho: TamanhoBotao) => (tamanho === "compacto" ? 16 : 20);

/**
 * Rótulo curto no celular do painel (abaixo de `lg`), p. ex. "Check-in" no lugar de "Registrar check-in" no
 * cabeçalho da página (DESIGN §3.3; pranchas treinando-agora-card e equipe-turnos a 390).
 */
function Rotulo({ curto, children }: { curto?: ReactNode; children?: ReactNode }) {
  if (curto === undefined) return <>{children}</>;
  return (
    <>
      <span className="lg:hidden">{curto}</span>
      <span className="max-lg:hidden">{children}</span>
    </>
  );
}

export function classesBotao(
  variante: VarianteBotao = "primario",
  tamanho: TamanhoBotao = "padrao",
  larguraTotal = false,
): string {
  return cn(
    BASE,
    VARIANTES[variante],
    !SEM_TAMANHO.has(variante) && TAMANHOS[tamanho],
    larguraTotal && variante !== "discreto" && "w-full",
  );
}

export function Girador({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block size-5 rounded-full border-2 border-current border-t-transparent animate-giro",
        className,
      )}
    />
  );
}

interface PropsComuns {
  variante?: VarianteBotao;
  tamanho?: TamanhoBotao;
  larguraTotal?: boolean;
  icone?: TipoIcone;
  /** Rótulo no celular do painel (abaixo de `lg`); o completo aparece do `lg` em diante. */
  rotuloCurto?: ReactNode;
  children?: ReactNode;
}

export interface PropsBotao extends PropsComuns, ButtonHTMLAttributes<HTMLButtonElement> {
  /** Mostra spinner e troca o texto (use gerúndio: "Entrando…"). */
  carregando?: boolean;
  textoCarregando?: string;
}

export const Botao = forwardRef<HTMLButtonElement, PropsBotao>(function Botao(
  {
    variante = "primario",
    tamanho = "padrao",
    larguraTotal,
    icone: Icone,
    carregando,
    textoCarregando,
    rotuloCurto,
    className,
    children,
    type = "button",
    disabled,
    ...resto
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || carregando}
      aria-busy={carregando || undefined}
      className={cn(classesBotao(variante, tamanho, larguraTotal), carregando && "!opacity-100", className)}
      {...resto}
    >
      {carregando ? (
        <>
          <Girador className={cn(tamanho === "compacto" && "size-4")} />
          <span>{textoCarregando ?? children}</span>
        </>
      ) : (
        <>
          {Icone && <Icone size={iconeDoTamanho(tamanho)} aria-hidden />}
          <Rotulo curto={rotuloCurto}>{children}</Rotulo>
        </>
      )}
    </button>
  );
});

/** Link com cara de botão (navegação interna). */
export function BotaoLink({
  variante = "primario",
  tamanho = "padrao",
  larguraTotal,
  icone: Icone,
  rotuloCurto,
  className,
  children,
  ...resto
}: PropsComuns & LinkProps) {
  return (
    <Link className={cn(classesBotao(variante, tamanho, larguraTotal), className)} {...resto}>
      {Icone && <Icone size={iconeDoTamanho(tamanho)} aria-hidden />}
      <Rotulo curto={rotuloCurto}>{children}</Rotulo>
    </Link>
  );
}

export interface PropsBotaoIcone extends ButtonHTMLAttributes<HTMLButtonElement> {
  icone: TipoIcone;
  /** Obrigatório: botão só-ícone precisa de nome acessível. */
  rotulo: string;
  forma?: "redondo" | "quadrado";
  variante?: "quieto" | "transparente" | "sobre-tinta";
  tamanhoIcone?: number;
}

/** Botão só-ícone 48×48 (DESIGN §5.1). */
export const BotaoIcone = forwardRef<HTMLButtonElement, PropsBotaoIcone>(function BotaoIcone(
  {
    icone: Icone,
    rotulo,
    forma = "redondo",
    variante = "transparente",
    tamanhoIcone = 24,
    className,
    type = "button",
    ...resto
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={rotulo}
      className={cn(
        "relative inline-grid size-toque shrink-0 place-items-center transition-colors duration-(--gs-dur-toque)",
        forma === "redondo" ? "rounded-full" : "rounded-controle",
        variante === "quieto" && "bg-recuo text-tinta hover:bg-fio",
        variante === "transparente" && "text-tinta hover:bg-recuo",
        variante === "sobre-tinta" && "bg-tinta-2 text-sobre-tinta foco-sobre-tinta",
        "disabled:opacity-40 disabled:pointer-events-none",
        className,
      )}
      {...resto}
    >
      <Icone size={tamanhoIcone} aria-hidden />
    </button>
  );
});
